/**
 * ObligationsEngineCard — iter84.61 · Release Ops Phase 1.
 *
 * Parallel to the Values Engine card: renders the obligations registry
 * grouped by obligation_group with a risk-if-deployed posture per
 * obligation and per group (compliance_state × severity, computed
 * server-side by /api/release-ops/obligations-registry).
 */
import { useCallback, useEffect, useState } from "react";
import { Scale, RefreshCw, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { CollapsibleCard } from "@/components/site-admin/CollapsibleCard";
import { CopyDiagButton, buildDiagText, diagLine } from "@/components/site-admin/release-ops/CopyDiagButton";
import { WhatsThisButton } from "@/components/site-admin/release-ops/WhatsThisButton";
import { POSTURE_PILL, SeverityChip } from "@/components/site-admin/ValuesAndUATCards";

const STATE_PILL = {
    "compliant":            "bg-emerald-900/40 text-emerald-200 border-emerald-700",
    "non-compliant":        "bg-rose-900/40 text-rose-200 border-rose-700",
    "investigating":        "bg-amber-900/40 text-amber-200 border-amber-700",
    "consult-professional": "bg-violet-900/40 text-violet-200 border-violet-700",
    "not-applicable":       "bg-zinc-800 text-zinc-400 border-zinc-700",
};

export function ObligationsEngineCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const fetchOnce = useCallback(async (spin) => {
        if (spin) setRefreshing(true);
        try {
            setData(await api.releaseOpsObligationsRegistry());
            setError(null);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to load obligations");
        } finally {
            setRefreshing(false);
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchOnce(false); }, [fetchOnce]);

    const posture = data?.overall_posture || "ok";
    const verdict = posture === "blocked" ? "fail" : posture === "warn" ? "warn" : "clear";
    const badge = data
        ? `${posture} · ${data.counts?.total_active ?? 0} active${data.review_due_count ? ` · ${data.review_due_count} due review` : ""}`
        : "loading";

    const groups = Object.entries(data?.obligation_groups || {})
        .filter(([, node]) => (node.children || []).length > 0)
        .sort(([, a], [, b]) => {
            const rank = { blocked: 0, warn: 1, notice: 2, ok: 3 };
            return (rank[a.posture] ?? 3) - (rank[b.posture] ?? 3);
        });

    return (
        <CollapsibleCard
            testId="obligations-engine-card"
            icon={Scale}
            title="Obligations Engine"
            badge={badge}
            badgeClass={POSTURE_PILL[posture] || POSTURE_PILL.ok}
            verdict={verdict}
            headerExtra={
                <span className="flex items-center gap-1">
                    <WhatsThisButton
                        testId="obligations-engine-whats-this"
                        title="Obligations Engine"
                        what="A registry of externally-imposed requirements (legal, platform, partner promises) and whether we believe we're compliant, with severity and review dates."
                        lookFor="Right now: treat it as a reference list, not a gate. Obligations haven't been mapped to live signals yet — it's informational by deliberate decision (2026-07-03) until that mapping exercise happens."
                        notFor="Blocking deploys. Nothing here gates ARM."
                    />
                    {data && (
                        <CopyDiagButton
                            testId="obligations-engine-copy-diag-btn"
                            getText={() => buildDiagText({
                                card: "Obligations Engine",
                                verdict: posture,
                                summary: `${data.counts?.total_active ?? 0} active${data.review_due_count ? ` · ${data.review_due_count} due review` : ""}`,
                                lines: Object.values(data.obligation_groups || {})
                                    .flatMap((node) => node.children || [])
                                    .filter((o) => o.risk_posture !== "ok")
                                    .map((o) => diagLine(o.risk_posture, `${o.name} [${o.severity} · ${o.compliance_state}]`, o.notes || o.description)),
                            })}
                        />
                    )}
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); fetchOnce(true); }}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); fetchOnce(true); } }}
                        data-testid="obligations-engine-refresh-btn"
                        className="brutal-btn-ghost text-[10px] py-1 px-2 inline-flex items-center gap-1 cursor-pointer"
                    >
                        {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        Refresh
                    </span>
                </span>
            }
        >
            {loading && (
                <p className="text-xs text-zinc-500 italic flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading obligations…
                </p>
            )}
            {error && <p className="text-xs text-rose-400" data-testid="obligations-engine-error">{error}</p>}
            {!loading && !error && data && (
                <div className="space-y-3" data-testid="obligations-engine-groups">
                    {groups.length === 0 && (
                        <p className="text-[11px] text-zinc-500 italic" data-testid="obligations-engine-empty">
                            No active obligations cataloged yet.
                        </p>
                    )}
                    {groups.map(([slug, node]) => (
                        <div
                            key={slug}
                            className="border border-zinc-800 rounded-md p-2 bg-black/30"
                            data-testid={`obligations-engine-group-${slug.replace(/[^a-z0-9-]/gi, "_")}`}
                        >
                            <div className="flex items-center gap-2 flex-wrap mb-1" title={node.statement}>
                                <span className="font-display font-extrabold text-[10px] uppercase tracking-wide text-cyan-200">
                                    {slug}
                                </span>
                                <span
                                    className={`pill text-[9px] font-bold uppercase border ${POSTURE_PILL[node.posture] || POSTURE_PILL.ok}`}
                                    data-testid={`obligations-engine-group-posture-${slug.replace(/[^a-z0-9-]/gi, "_")}`}
                                >
                                    {node.posture === "blocked" ? "risk: blocked" : node.posture}
                                </span>
                            </div>
                            <ul className="space-y-1">
                                {node.children.map((o) => (
                                    <li
                                        key={o.name}
                                        className="flex items-start gap-2 text-[11px] py-1 border-b border-[#1a1a1a] last:border-b-0"
                                        data-testid={`obligations-engine-row-${o.name}`}
                                    >
                                        <span
                                            className={`pill text-[9px] font-bold uppercase border shrink-0 ${POSTURE_PILL[o.risk_posture] || POSTURE_PILL.ok}`}
                                            data-testid={`obligations-engine-posture-${o.name}`}
                                        >
                                            {o.risk_posture}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-bold text-white">{o.name}</span>
                                                <SeverityChip severity={o.severity} />
                                                <span className={`pill text-[9px] font-bold uppercase border ${STATE_PILL[o.compliance_state] || STATE_PILL["not-applicable"]}`}>
                                                    {o.compliance_state}
                                                </span>
                                            </div>
                                            <p className="text-[10px] text-zinc-400 mt-0.5 leading-relaxed">{o.notes || o.description}</p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                    <p className="text-[9px] text-zinc-600">
                        Posture = compliance_state × severity. Severity is governed in Stewardship → Severity;
                        state transitions happen in Stewardship → Obligations Review.
                    </p>
                </div>
            )}
        </CollapsibleCard>
    );
}
